import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bug, Paperclip, X, Loader2, FileText, MessageSquare } from 'lucide-react';
import Modal from '../Modal/Modal';
import Toast from '../Toast/Toast';
import { bugService } from '../../../services/bug.service';
import { useBugReports } from '../../../context/BugReportContext.jsx';
import { FRONTEND_BUILD, FRONTEND_VERSION } from '@shared/appVersion.js';
import './ReportBugButton.css';

const MAX_ATTACHMENTS = 5;
// Pop-in + attention pulses on first paint, so the button gets noticed
const INTRO_DURATION = 4200;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const VALID_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'application/pdf',
];

const ReportBugButton = () => {
  const { refreshCounts, unreadCount } = useBugReports();
  const [isOpen, setIsOpen] = useState(false);
  const [issue, setIssue] = useState('');
  const [newsUrl, setNewsUrl] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [intro, setIntro] = useState(true);
  const fileInputRef = useRef(null);

  // Drop the intro class once it has played so hover/press transitions stay snappy
  useEffect(() => {
    const timer = setTimeout(() => setIntro(false), INTRO_DURATION);
    return () => clearTimeout(timer);
  }, []);

  const resetForm = () => {
    setIssue('');
    setNewsUrl('');
    setAttachments([]);
  };

  const closeModal = () => {
    if (submitting || uploading) return;
    setIsOpen(false);
    resetForm();
  };

  // Upload a single file to GCS using a signed URL
  const uploadFile = async (file) => {
    const response = await bugService.getUploadSignedUrl(file.name, file.type, file.size);

    if (!response.success) {
      throw new Error(response.message || 'Failed to get upload URL');
    }

    const { signedUrl, publicUrl } = response.data;

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.onload = () => (xhr.status === 200 ? resolve() : reject(new Error('Upload failed')));
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });

    return { url: publicUrl, fileName: file.name, contentType: file.type };
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Allow re-selecting the same file after a remove
    e.target.value = '';

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      setToast({ message: `You can attach up to ${MAX_ATTACHMENTS} files`, type: 'error' });
      return;
    }

    const invalidType = files.find((file) => !VALID_TYPES.includes(file.type));
    if (invalidType) {
      setToast({ message: 'Only images and PDF files are allowed', type: 'error' });
      return;
    }

    const tooLarge = files.find((file) => file.size > MAX_FILE_SIZE);
    if (tooLarge) {
      setToast({ message: 'Each file must be 10MB or smaller', type: 'error' });
      return;
    }

    setUploading(true);
    try {
      const uploaded = await Promise.all(files.map(uploadFile));
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setToast({ message: 'Failed to upload attachment', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (url) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.url !== url));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!issue.trim()) {
      setToast({ message: 'Please explain the issue', type: 'error' });
      return;
    }

    if (uploading) {
      setToast({ message: 'Please wait for attachments to finish uploading', type: 'info' });
      return;
    }

    setSubmitting(true);
    try {
      const response = await bugService.reportBug({
        issue: issue.trim(),
        newsUrl: newsUrl.trim(),
        attachments,
        pageUrl: window.location.href,
        // The build this tab is running - a stale tab is a common cause of
        // "bugs", and only the client knows which build it has
        frontendBuild: FRONTEND_BUILD,
        frontendVersion: FRONTEND_VERSION,
      });

      if (response.success) {
        setIsOpen(false);
        resetForm();
        setToast({ message: 'Bug reported successfully. Thank you!', type: 'success' });
        // Keep the sidebar open-bugs badge current
        refreshCounts();
      } else {
        setToast({ message: response.message || 'Failed to report bug', type: 'error' });
      }
    } catch (err) {
      console.error('Error reporting bug:', err);
      setToast({
        message: err.response?.data?.message || 'Failed to report bug',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`report-bug-fab ${intro ? 'report-bug-fab--intro' : ''}`}
        onClick={() => {
          setIntro(false);
          setIsOpen(true);
        }}
        aria-label={
          unreadCount > 0
            ? `Report a bug - ${unreadCount} unread ${unreadCount > 1 ? 'messages' : 'message'}`
            : 'Report a bug'
        }
        title={
          unreadCount > 0
            ? `${unreadCount} unread ${unreadCount > 1 ? 'messages' : 'message'} on your bug reports`
            : 'Report a bug'
        }
      >
        <Bug size={18} className="report-bug-fab-icon" />
        <span className="report-bug-fab-label">Report Bug</span>
        {/* Stays on the button until the user opens the thread */}
        {unreadCount > 0 && (
          <span className="report-bug-fab-badge">+{unreadCount > 99 ? 99 : unreadCount}</span>
        )}
      </button>

      <Modal isOpen={isOpen} onClose={closeModal} title="Report a Bug" size="medium">
        {unreadCount > 0 && (
          <Link
            to="/dashboard/bug-reports"
            className="report-bug-unread-banner"
            onClick={closeModal}
          >
            <MessageSquare size={15} />
            <span>
              You have {unreadCount} unread {unreadCount > 1 ? 'messages' : 'message'} on your bug{' '}
              {unreadCount > 1 ? 'reports' : 'report'} - open the conversation
            </span>
          </Link>
        )}

        <form className="report-bug-form" onSubmit={handleSubmit}>
          <div className="report-bug-field">
            <label className="report-bug-label" htmlFor="bug-issue">
              Explain issue <span className="report-bug-required">*</span>
            </label>
            <textarea
              id="bug-issue"
              className="report-bug-textarea"
              placeholder="Explain issue - what went wrong, what did you expect to happen?"
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
              rows={5}
              autoFocus
            />
          </div>

          <div className="report-bug-field">
            <label className="report-bug-label" htmlFor="bug-news-url">
              News URL
            </label>
            <input
              id="bug-news-url"
              type="text"
              className="report-bug-input"
              placeholder="News URL (if the issue is with a specific news)"
              value={newsUrl}
              onChange={(e) => setNewsUrl(e.target.value)}
            />
          </div>

          <div className="report-bug-field">
            <label className="report-bug-label">Attach proof</label>
            <input
              ref={fileInputRef}
              type="file"
              className="report-bug-file-input"
              accept="image/*,application/pdf"
              multiple
              onChange={handleFileSelect}
            />
            <button
              type="button"
              className="report-bug-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || attachments.length >= MAX_ATTACHMENTS}
            >
              {uploading ? <Loader2 size={15} className="spin" /> : <Paperclip size={15} />}
              {uploading ? 'Uploading...' : 'Attach proof (screenshot or PDF)'}
            </button>
            <span className="report-bug-hint">
              Up to {MAX_ATTACHMENTS} files, 10MB each
            </span>

            {attachments.length > 0 && (
              <ul className="report-bug-attachments">
                {attachments.map((attachment) => (
                  <li key={attachment.url} className="report-bug-attachment">
                    {attachment.contentType?.startsWith('image/') ? (
                      <img src={attachment.url} alt="" className="report-bug-attachment-thumb" />
                    ) : (
                      <span className="report-bug-attachment-icon">
                        <FileText size={15} />
                      </span>
                    )}
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noreferrer"
                      className="report-bug-attachment-name"
                    >
                      {attachment.fileName}
                    </a>
                    <button
                      type="button"
                      className="report-bug-attachment-remove"
                      onClick={() => handleRemoveAttachment(attachment.url)}
                      aria-label={`Remove ${attachment.fileName}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="report-bug-actions">
            <button
              type="button"
              className="report-bug-cancel"
              onClick={closeModal}
              disabled={submitting || uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="report-bug-submit"
              disabled={submitting || uploading}
            >
              {submitting ? <Loader2 size={15} className="spin" /> : 'Submit'}
            </button>
          </div>
        </form>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ReportBugButton;
